import * as marietta from '../../..';
import { Dialog } from '../../../core/view';
import { Tool } from '../ToolMenu';
import { DebugSession, DebugToken } from './DebugSession';

// Webサイトと同じログインAPI・送信形式（FormData）。/api/v1/app/login のトークンは物件一覧APIで401になるため使わない
const LOGIN_PATH = '/api/v1/login';
const LOGIN_METHOD = 'POST';
const FORM_KEY_EMAIL = 'email';
const FORM_KEY_PASSWORD = 'password';
const JSON_CONTENT_TYPE = 'application/json';
const TOOL_NAME = 'LOGIN';
const DIALOG_ID = 'debugLoginDialog';
const DIALOG_TITLE = 'ログイン（デバッグ）';
const EMAIL_INPUT_ID = 'debugLoginEmail';
const PASSWORD_INPUT_ID = 'debugLoginPassword';
const LOGIN_BUTTON_ID = 'btnDebugLogin';
const LOGOUT_BUTTON_ID = 'btnDebugLogout';
const CANCEL_BUTTON_ID = 'btnDebugLoginCancel';
const LABEL_EMAIL = 'メールアドレス';
const LABEL_PASSWORD = 'パスワード';
const LABEL_LOGIN = 'ログイン';
const LABEL_LOGOUT = 'ログアウト';
const LABEL_CANCEL = 'キャンセル';
const ALERT_TITLE = 'デバッグ';
const MSG_LOGIN_SUCCESS = 'ログインしました。続けて「プラン選択」から読み込むプランを選んでください。';
const MSG_LOGIN_FAILED = 'ログインに失敗しました。';
const MSG_LOGOUT = 'ログアウトしました（保存したトークンを削除しました）。表示中の画面にはリロード後に反映されます。';
const STATUS_LOGGED_IN = 'ログイン中（有効期限: {0}）';
const STATUS_LOGGED_OUT = '未ログイン';
const EXPIRES_PLACEHOLDER = '{0}';
const CONTAINER_STYLE = 'width:320px;display:flex;flex-direction:column;gap:12px;padding:12px;box-sizing:border-box;';
const FIELD_STYLE = 'display:flex;flex-direction:column;gap:4px;';
const INPUT_STYLE = 'box-sizing:border-box;padding:4px;';
const CLOSE_DIV = '</div>';
const KEYDOWN_EVENT = 'keydown';
const INPUT_SELECTOR = 'input';

export class DebugLogin implements Tool {
  public readonly name: string = TOOL_NAME;

  public execute(): void {
    marietta.webcad.WebCADGlobal.showDialog(DebugLoginDialog, true);
  }
}

class DebugLoginDialog extends Dialog {
  public constructor(draggable: boolean, hasModal: boolean) {
    super(draggable, hasModal);
  }

  public buildContent(): void {
    const token = DebugSession.getToken();
    const status = token
      ? STATUS_LOGGED_IN.replace(EXPIRES_PLACEHOLDER, new Date(token.expires_in).toLocaleString())
      : STATUS_LOGGED_OUT;

    const contents = `<div style="${CONTAINER_STYLE}">`
      + `<div>${status}</div>`
      + `<div style="${FIELD_STYLE}">`
      + `<label for="${EMAIL_INPUT_ID}">${LABEL_EMAIL}</label>`
      + `<input type="text" id="${EMAIL_INPUT_ID}" autocomplete="username" style="${INPUT_STYLE}">`
      + CLOSE_DIV
      + `<div style="${FIELD_STYLE}">`
      + `<label for="${PASSWORD_INPUT_ID}">${LABEL_PASSWORD}</label>`
      + `<input type="password" id="${PASSWORD_INPUT_ID}" autocomplete="current-password" style="${INPUT_STYLE}">`
      + CLOSE_DIV
      + CLOSE_DIV;

    let footer = `<button id="${LOGIN_BUTTON_ID}">${LABEL_LOGIN}</button>`;
    if (token) footer += `<button id="${LOGOUT_BUTTON_ID}">${LABEL_LOGOUT}</button>`;
    footer += `<button id="${CANCEL_BUTTON_ID}">${LABEL_CANCEL}</button>`;

    this.createDialog(DIALOG_ID, DIALOG_TITLE, contents, footer);

    // 入力中のキーが作図ショートカット（Ctrl+Z / Ctrl+V / Space 等）に渡らないようにする（既存 LoginDialog と同じ）
    this.viewObj.querySelectorAll(INPUT_SELECTOR).forEach((el) => {
      el.addEventListener(KEYDOWN_EVENT, (e) => e.stopImmediatePropagation());
    });
    document.getElementById(LOGIN_BUTTON_ID)?.addEventListener('click', this.login);
    document.getElementById(LOGOUT_BUTTON_ID)?.addEventListener('click', this.logout);
    document.getElementById(CANCEL_BUTTON_ID)?.addEventListener('click', this.close);
  }

  private readonly login = async (): Promise<void> => {
    const loginButton = document.getElementById(LOGIN_BUTTON_ID) as HTMLButtonElement;
    // 連打で通信が二重に走り close が2回呼ばれるのを防ぐ
    if (loginButton.disabled) return;
    loginButton.disabled = true;
    const body = new FormData();
    body.append(FORM_KEY_EMAIL, (document.getElementById(EMAIL_INPUT_ID) as HTMLInputElement).value);
    body.append(FORM_KEY_PASSWORD, (document.getElementById(PASSWORD_INPUT_ID) as HTMLInputElement).value);
    const url = `${marietta.webcad.WebCADGlobal.webcad2Dmanager.domain}${LOGIN_PATH}`;

    try {
      const response = await fetch(url, { method: LOGIN_METHOD, body, headers: { Accept: JSON_CONTENT_TYPE } });
      if (!response.ok) throw new Error(response.statusText);

      const token: DebugToken = await response.json();
      if (!token.access_token) throw new Error(MSG_LOGIN_FAILED);
      if (!this.viewObj.isConnected) return;

      // パスワードは保存しない。トークンだけ保存し、現在の画面にも即時反映する
      DebugSession.saveToken(token);
      marietta.webcad.WebCADGlobal.webcad2Dmanager.access_token = DebugSession.getAuthorization();
      this.close();
      marietta.webcad.WebCADGlobal.showAlert(MSG_LOGIN_SUCCESS, ALERT_TITLE);
    } catch (e) {
      console.error(e);
      if (!this.viewObj.isConnected) return;
      loginButton.disabled = false;
      marietta.webcad.WebCADGlobal.showAlert(MSG_LOGIN_FAILED, ALERT_TITLE);
    }
  };

  private readonly logout = (): void => {
    DebugSession.clearToken();
    this.close();
    marietta.webcad.WebCADGlobal.showAlert(MSG_LOGOUT, ALERT_TITLE);
  };
}
